<script lang="ts">
  import { internalLink } from '$lib/utils/internalLink';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { onDestroy } from 'svelte';
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { createTouchpointDeleter, deletableTouchpoint } from '$lib/utils/deleteTouchpoint';
  import { activitiesApi } from '$lib/api/crm';
  import { touchpointsApi } from '$lib/api/touchpoints';
  import { apiFetch, getErrorMessage } from '$lib/api/client';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { istDay, istInput, istTime, dayOffset, dayHeading, nextMonday, shortDay, editableTouchpoint, touchpointClock, touchpointTypeLabel, outcomeLabel, outcomeTags, plural } from '$lib/utils/touchpoints';
  import type { Activity } from '$lib/types';
  import Composer from './Composer.svelte';
  import AgendaBucket from './AgendaBucket.svelte';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import { consumeContactCall } from '$lib/utils/pendingCall';

  const filters = $derived(route.current.tab === 'activities' ? route.current.filters ?? {} : {});
  const view = $derived(filters.view === 'calendar' || filters.view === 'log' ? filters.view : 'agenda');
  const scope = $derived(filters.scope ?? 'mine');
  const vertical = $derived(session.selectedVertical === 'all' ? undefined : session.selectedVertical);
  const params = $derived({ vertical_id: vertical, scope, q: filters.q, type: filters.type, assignee_ids: filters.assignee_ids, outcome_tag: filters.outcome_tag });
  const counts = createQuery(() => ({ queryKey: ['agenda-counts', params], queryFn: () => touchpointsApi.buckets(params) }));
  let show = $state(false), editing = $state<Activity | null>(null), mode = $state<'log' | 'plan'>('log'), type = $state<string | undefined>(), drawer = $state(false), page = $state(1), handled = $state<number>(), composerPersonId = $state<number | undefined>();
  const selectedId = $derived(route.current.tab === 'activities' ? route.current.id : undefined);
  const selected = createQuery(() => ({ queryKey: ['activity', selectedId], queryFn: () => activitiesApi.get(selectedId!), enabled: !!selectedId }));
  const client = useQueryClient();
  let deleteBusy = $state(false), deleteError = $state(''), editorVersion = $state(0);
  let active = true;
  onDestroy(() => { active = false; });
  const deleteLog = createTouchpointDeleter({
    confirm: confirmAction, remove: activitiesApi.remove, invalidate: () => client.invalidateQueries(),
    removeDraft: key => localStorage.removeItem(key), setBusy: value => { deleteBusy = value; },
    setError: err => { deleteError = err ? getErrorMessage(err) : ''; }
  });
  function removeLog(row: Activity) {
    const currentRoute = route.current, version = editorVersion, targetId = row.id, userId = session.me?.user.id;
    return deleteLog(row, session.me?.user.id ?? 0, {
      isCurrent: () => active && route.current === currentRoute && editorVersion === version && session.me?.user.id === userId,
      onDeleted: () => { if (show && editing?.id === targetId) { show = false; editorVersion++; } }
    });
  }

  function open(row: Activity | null = null, nextMode: 'log' | 'plan' = 'log', newConversation = false) {
    editorVersion++;
    composerPersonId = newConversation ? row?.person_id ?? undefined : undefined;
    editing = newConversation && row?.status !== 'scheduled' ? null : row;
    mode = nextMode;
    type = undefined;
    show = true;
  }
  $effect(() => {
    if (selected.data && selectedId !== handled) {
      handled = selectedId;
      open(selected.data, selected.data.status === 'scheduled' ? 'plan' : 'log');
    }
  });
  $effect(() => {
    const signal = session.consumeSignal('activities');
    if (signal?.mode === 'create') {
      editorVersion++;
      editing = null;
      composerPersonId = typeof signal.prefill?.person_id === 'number' ? signal.prefill.person_id : undefined;
      mode = signal.prefill?.mode === 'plan' ? 'plan' : 'log';
      type = typeof signal.prefill?.type === 'string' ? signal.prefill.type : undefined;
      show = true;
    } else if (signal?.mode === 'edit') {
      open(signal.record as Activity, (signal.record as Activity).status === 'scheduled' ? 'plan' : 'log');
    }
  });
  function setFilter(key: string, value: string) {
    const next = { ...filters, [key]: value };
    if (!value) delete next[key as keyof typeof next];
    route.replace({ tab: 'activities', filters: next });
    page = 1;
  }

  const today = istDay();
  const weekStart = $derived(filters.from ?? dayOffset(-((new Date(`${today}T12:00:00Z`).getUTCDay() + 6) % 7), today));
  const days = $derived(Array.from({ length: 6 }, (_, i) => dayOffset(i, weekStart)));
  const hours = Array.from({ length: 13 }, (_, i) => i + 8);
  const calendar = createQuery(() => ({
    queryKey: ['agenda-calendar', params, weekStart],
    queryFn: () => touchpointsApi.list({ ...params, view: 'calendar', status: 'scheduled', from: weekStart, to: dayOffset(5, weekStart), per_page: 200 }),
    enabled: view === 'calendar'
  }));
  const log = createQuery(() => ({ queryKey: ['touchpoint-log', params, page], queryFn: () => touchpointsApi.list({ ...params, view: 'log', page, per_page: 30 }), enabled: view === 'log' }));
  const emailAccess = $derived(!session.me?.user.issues_only && !session.me?.user.dispatch_only && (session.isAdmin || !!session.me?.user.email_access));
  const logRows = $derived((log.data?.results ?? []).filter(row => emailAccess || row.type !== 'email'));
  const assignees = createQuery(() => ({
    queryKey: ['agenda-assignees', vertical, session.me?.user.id, session.me?.verticals.map(v => v.id).join(',')],
    queryFn: async () => {
      const ids = vertical ? [vertical] : (session.me?.verticals ?? []).map(v => v.id);
      const groups = await Promise.all(ids.map(id => apiFetch<Array<{ id: number; name: string }>>(`/activities/assignees?vertical_id=${id}`)));
      return [...new Map(groups.flat().map(user => [user.id, user])).values()];
    },
    enabled: true
  }));
  let phoneDay = $state(today);
  function calendarRows(day: string, hour: number) {
    return (calendar.data?.results ?? [])
      .filter(row => row.scheduled_at && istDay(row.scheduled_at) === day && Number(istInput(row.scheduled_at).slice(11, 13)) === hour)
      .map(row => ({ ...row, assignee_name: row.assignee_name ?? assignees.data?.find(u => u.id === row.assignee_user_id)?.name ?? (row.assignee_user_id ? 'Former team member' : 'Unassigned') }));
  }
  function keyboard(e: KeyboardEvent) {
    if (show || (e.target as HTMLElement)?.closest('input,textarea,select,[contenteditable=true],[data-tym-modal]') || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key.toLowerCase() === 'l') open();
    if (e.key.toLowerCase() === 's') open(null, 'plan');
  }
  function returned() {
    const call = consumeContactCall();
    if (call) { editorVersion++; editing = null; composerPersonId = call.personId; type = call.type; mode = 'log'; show = true; }
  }
  function moveWeek(delta: number) {
    phoneDay = dayOffset(delta, weekStart);
    setFilter('from', phoneDay);
  }
  const typeFilters = ['call', 'meeting', 'visit', 'task', 'note', 'whatsapp'];
  const viewLabels: Array<[string, string]> = [['agenda', 'Agenda'], ['calendar', 'Calendar'], ['log', 'Log']];
  const bucketLabels: Array<[string, string]> = [['overdue', 'Overdue'], ['today', 'Today'], ['tomorrow', 'Tomorrow'], ['week', 'Later this week'], ['later', 'Later']];
</script>

<svelte:window onkeydown={keyboard} onfocus={returned} />
<div class="tym-page agenda">
  <div class="toolbar">
    <div class="day-segment views" role="group" aria-label="Touchpoint view">
      {#each viewLabels as [key, label]}<button type="button" aria-pressed={view === key} onclick={() => setFilter('view', key === 'agenda' ? '' : key)}>{label}</button>{/each}
    </div>
    <select class="input scope" aria-label="Responsibility scope" value={scope} onchange={e => setFilter('scope', e.currentTarget.value)}>
      <option value="mine">Mine</option>
      <option value="team">Whole team</option>
      <option value="unassigned">Unassigned</option>
    </select>
    <input class="input search" type="search" aria-label="Search touchpoints" value={filters.q ?? ''} oninput={e => setFilter('q', e.currentTarget.value)} placeholder="Search…" />
    <button type="button" class="btn-secondary filter-btn" aria-expanded={drawer} onclick={() => drawer = !drawer}>Filters{filters.type || filters.assignee_ids || filters.outcome_tag ? ' •' : ''}</button>
    <button type="button" class="btn-primary log-btn" onclick={() => open()}>＋ Log or plan</button>
  </div>

  {#if drawer}
    <section class="day-card filters" aria-label="Touchpoint filters">
      <label class="field">Type
        <select class="input" value={filters.type ?? ''} onchange={e => setFilter('type', e.currentTarget.value)}>
          <option value="">All types</option>
          {#each [...typeFilters, ...(emailAccess ? ['email'] : [])] as key}<option value={key}>{touchpointTypeLabel(key)}</option>{/each}
        </select>
      </label>
      <label class="field">Who's going <small>(Ctrl/⌘-click to pick several)</small>
        <select class="input" multiple value={(filters.assignee_ids ?? '').split(',')} onchange={e => setFilter('assignee_ids', [...e.currentTarget.selectedOptions].map(o => o.value).join(','))}>
          {#each assignees.data ?? [] as user (user.id)}<option value={String(user.id)}>{user.name}</option>{/each}
        </select>
      </label>
      <label class="field">Outcome
        <select class="input" value={filters.outcome_tag ?? ''} onchange={e => setFilter('outcome_tag', e.currentTarget.value)}>
          <option value="">All outcomes</option>
          {#each outcomeTags as key}<option value={key}>{outcomeLabel(key)}</option>{/each}
        </select>
      </label>
      <div class="filter-actions">
        <button type="button" class="day-link" onclick={() => route.replace({ tab: 'activities', filters: { view: filters.view, scope: filters.scope } })}>Clear filters</button>
        <button type="button" class="day-mini-btn" onclick={() => drawer = false}>Done</button>
      </div>
    </section>
  {/if}
  {#if counts.isError}<p class="tym-error-state" role="alert">Section totals unavailable: {getErrorMessage(counts.error)}</p>{/if}
  {#if deleteError}<p class="tym-error-state" role="alert">{deleteError}</p>{/if}

  {#if view === 'agenda'}
    <div class="buckets">
      {#each bucketLabels as [bucket, label] (bucket)}<AgendaBucket {bucket} {label} count={counts.data?.[bucket]} {params} onOpen={open} />{/each}
    </div>
  {:else if view === 'calendar'}
    <div class="day-card week">
      <div class="week-nav">
        <button type="button" class="day-mini-btn" onclick={() => moveWeek(-7)}>← Previous</button>
        <strong class="day-title">{shortDay(weekStart)} – {shortDay(dayOffset(5, weekStart))}</strong>
        <button type="button" class="day-mini-btn" onclick={() => moveWeek(7)}>Next →</button>
        {#if weekStart !== dayOffset(-((new Date(`${today}T12:00:00Z`).getUTCDay() + 6) % 7), today)}<button type="button" class="day-link" onclick={() => setFilter('from', '')}>This week</button>{/if}
        <input class="input phone-picker" aria-label="Calendar day" type="date" bind:value={phoneDay} onchange={() => { if (phoneDay) setFilter('from', dayOffset(-7, nextMonday(phoneDay))); }} />
      </div>
      <p class="day-meta">Monday–Saturday, 08:00–21:00 IST · select a block to open it.</p>
      {#if calendar.isError}<p class="tym-error-state" role="alert">{getErrorMessage(calendar.error)}</p>{/if}
      {#if calendar.isPending}<p class="tym-loading-state">Loading calendar…</p>{/if}
      {#if calendar.data && calendar.data.count > calendar.data.results.length}<p class="day-meta">Showing {calendar.data.results.length} of {calendar.data.count} plans — narrow Who's going to see every block.</p>{/if}
      <div class="calendar">
        <div class="week-head"><span></span>{#each days as day}<strong class:today={day === today}>{shortDay(day)}</strong>{/each}</div>
        {#each hours as hour}
          <div class="hour">
            <span>{String(hour).padStart(2, '0')}:00</span>
            {#each days as day}
              <div class="cell" class:today={day === today}>
                {#each calendarRows(day, hour) as row (row.id)}
                  <button type="button" class="block" onclick={() => open(row, 'plan')}>
                    <strong>{istTime(row.scheduled_at!)} · {row.person_name || row.title}</strong>
                    <small>{touchpointTypeLabel(row.type)} · {row.assignee_name}</small>
                  </button>
                {/each}
              </div>
            {/each}
          </div>
        {/each}
      </div>
      <div class="phone-calendar">
        <p class="day-title">{dayHeading(phoneDay)}</p>
        {#each hours as hour}
          <div class="phone-hour">
            <span>{String(hour).padStart(2, '0')}:00</span>
            <div>
              {#each calendarRows(phoneDay, hour) as row (row.id)}
                <button type="button" class="block" onclick={() => open(row, 'plan')}><strong>{istTime(row.scheduled_at!)} · {row.person_name || row.title}</strong><small>{touchpointTypeLabel(row.type)} · {row.assignee_name}</small></button>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    </div>
  {:else}
    <p class="day-meta">{log.data ? plural(log.data.count, 'completed touchpoint') : 'Completed touchpoints'}{emailAccess ? ', including synced emails' : ''}</p>
    {#if log.isPending}<p class="tym-loading-state">Loading log…</p>{:else if log.isError}<p class="tym-error-state" role="alert">{getErrorMessage(log.error)}</p>{/if}
    <div class="log">
      {#each logRows as row, i (row.id)}
        {#if i === 0 || istDay(touchpointClock(row)) !== istDay(touchpointClock(logRows[i - 1]))}<h2 class="log-day">{dayHeading(istDay(touchpointClock(row)))}</h2>{/if}
        <article class="day-card entry">
          <div class="time"><strong>{istTime(touchpointClock(row))}</strong><span>{touchpointTypeLabel(row.type)}</span></div>
          <div class="content">
            {#if row.person_id}<a class="person" href={`#/people/${row.person_id}`} use:internalLink={{ tab: 'people', id: row.person_id }}>{row.person_name || 'Contact'}{#if row.company_name}<span class="day-meta"> · {row.company_name}</span>{/if}</a>{:else}<p class="person">{row.company_name || 'No contact linked'}</p>{/if}
            <p class="what">{row.title}</p>
            {#if row.outcome_tags?.length}<div class="chips">{#each row.outcome_tags as t}<span class="day-chip day-chip-info">{outcomeLabel(t)}</span>{/each}</div>{/if}
            <p class="day-meta small" class:recorder={row.type !== 'email'}>
              {#if row.type === 'email'}Synced email · read-only{:else}{row.assignee_user_id ? `Handled by ${row.assignee_name ?? 'former team member'} · ` : ''}Recorded by {row.booked_by_name || row.created_by}{/if}
            </p>
          </div>
          <div class="row-actions">
            {#if editableTouchpoint(row)}<button type="button" class="day-mini-btn" onclick={() => open(row)}>Edit</button>{/if}
            {#if deletableTouchpoint(row)}<button type="button" class="day-mini-btn" disabled={deleteBusy} aria-label={`Delete manual log #${row.id}: ${row.title}`} onclick={() => removeLog(row)}>{deleteBusy ? 'Deleting…' : 'Delete'}</button>{/if}
          </div>
        </article>
      {:else}
        {#if !log.isPending}<p class="tym-empty-state">No completed touchpoints match.</p>{/if}
      {/each}
    </div>
    {#if log.data}<Pagination {page} perPage={30} count={log.data.count} onChange={p => page = p} />{/if}
  {/if}
</div>
{#if show}<Composer personId={composerPersonId} activity={editing} {mode} initialType={type} onClose={() => { editorVersion++; show = false; if (selectedId) route.replace({ tab: 'activities', filters }); }} />{/if}

<style>
  .agenda { display: grid; gap: 14px; }
  .toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
  .scope { width: 230px; }
  .search { flex: 1; min-width: 180px; }
  .field { display: grid; gap: 6px; font-size: 12.5px; font-weight: 600; color: var(--tym-navy); min-width: 200px; }
  .field small { font-weight: 400; color: var(--tym-dark-gray); }
  .filters { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-end; }
  .filter-actions { display: flex; gap: 12px; align-items: center; margin-left: auto; }
  .buckets { display: grid; gap: 12px; }
  .week { display: grid; gap: 10px; }
  .week-nav { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .calendar { overflow: auto; border: 1px solid var(--tym-cloudy-gray); border-radius: var(--tym-radius-md); }
  .week-head, .hour { display: grid; grid-template-columns: 60px repeat(6, minmax(120px, 1fr)); min-width: 820px; }
  .week-head > * { padding: 10px 6px; font-size: 12px; color: var(--tym-navy); }
  .week-head .today { color: var(--tym-teal); }
  .hour > span { padding: 8px; font-size: 11px; color: var(--tym-dark-gray); }
  .cell { min-height: 64px; border-top: 1px solid #efefec; border-left: 1px solid #efefec; padding: 3px; }
  .cell.today { background: #fbfdf6; }
  .block { display: grid; gap: 1px; width: 100%; min-height: 40px; margin-bottom: 3px; padding: 6px 8px; text-align: left; font-size: 12px; border-radius: var(--tym-radius-sm); background: var(--tym-teal-100); border-left: 3px solid var(--tym-teal); color: var(--tym-navy); }
  .block:hover { background: #d5e8ea; }
  .block small { color: var(--tym-teal-600); }
  .phone-calendar, .phone-picker { display: none; }
  .log { display: grid; gap: 8px; }
  .log-day { margin-top: 8px; font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--tym-dark-gray); }
  .entry { display: flex; gap: 16px; align-items: flex-start; padding: 14px 18px; }
  .time { flex: none; width: 76px; display: grid; gap: 2px; }
  .time strong { font-family: var(--tym-font-display); color: var(--tym-navy); }
  .time span { font-size: 12px; font-weight: 600; color: var(--tym-teal); }
  .content { flex: 1; min-width: 0; display: grid; gap: 4px; }
  .person { font-weight: 700; color: var(--tym-navy); }
  a.person:hover { color: var(--tym-teal); }
  .what { font-size: 13.5px; }
  .small { font-size: 12px; }
  .chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .row-actions { display: flex; flex-wrap: wrap; gap: 6px; }
  @media (max-width: 767px) {
    .agenda { gap: 10px; }
    .toolbar { gap: 8px; }
    .views { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); width: 100%; border-radius: 14px; padding: 4px; }
    .views button { min-height: 36px; border-radius: 10px; font-size: 13px; }
    .scope { width: auto; min-width: 0; flex: 1 1 0; }
    .search { min-width: 0; flex: 1 1 0; }
    .filter-btn { flex: none; padding-left: 12px; padding-right: 12px; }
    /* The bottom bar's Log button covers this on phones. */
    .log-btn { display: none; }
    .log { gap: 0; border-radius: 18px; overflow: hidden; background: var(--tym-white); box-shadow: 0 1px 2px rgba(16, 34, 58, 0.06); }
    .log-day { margin: 0; padding: 14px 14px 6px; background: var(--tym-light-carbon); }
    .entry { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 2px 10px; padding: 12px 14px; border-radius: 0; box-shadow: none; border-bottom: 1px solid #f0f0ee; }
    .time { width: auto; grid-column: 1; grid-row: 1; display: flex; gap: 6px; font-size: 12px; }
    .time strong { font-size: 12px; font-weight: 600; color: var(--tym-dark-gray); }
    .content { grid-column: 1; grid-row: 2; }
    .entry > .row-actions { grid-column: 2; grid-row: 1 / span 2; align-self: center; flex-direction: column; }
    .recorder { display: none; }
  }
  @media (max-width: 680px) {
    .calendar { display: none; }
    .phone-calendar { display: grid; gap: 4px; }
    .phone-picker { display: block; width: auto; }
    .phone-hour { min-height: 56px; border-top: 1px solid #efefec; display: grid; grid-template-columns: 50px 1fr; gap: 8px; padding: 6px 0; }
    .phone-hour > span { font-size: 12px; color: var(--tym-dark-gray); }
    .entry { flex-wrap: wrap; }
  }
</style>
